/**
 * BulkImportModal
 *
 * Migrate brokerage records into the CRM from a CSV or XLSX file.
 * Two-step UX: pick file → optional dry-run (validate without
 * inserting) → real import. Every row's outcome is reported back —
 * created vs. duplicate-skipped vs. error — so the user can act on
 * the conflicts (rename, merge, delete) rather than discover them
 * months later.
 */
import React, { useRef, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogBody, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Upload, Download, FileSpreadsheet, Loader2, CheckCircle2,
  AlertTriangle, XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../../contexts/AuthContext';
import { bulkImportBrokerages, importTemplateXlsxUrl } from '../api/brokerages';
import InfoBadge from '@/components/InfoBadge';

// Human-readable labels + explanations for each conflict_field the
// backend can return. Used as the tooltip on each duplicate-row chip
// so the user understands *why* a row was rejected, not just that it
// was. Keep these aligned with the conflict_field strings in
// routes/brokerage_hub_routes.py.
const CONFLICT_FIELD_INFO = {
  name:           'The brokerage name matches an existing firm (case-insensitive).',
  rera_license:   'The RERA licence / RERA number matches an existing firm.',
  trade_license:  'The trade licence matches an existing firm.',
  email:          'The contact email matches an existing firm (after lowercase / trim).',
  phone:          'The last 9 digits of the phone number match an existing firm (UAE normalization — handles +971 / 971 / 0 prefixes).',
};

export default function BulkImportModal({ open, onOpenChange, onImported }) {
  const { token } = useAuth();
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [running, setRunning] = useState(false);
  const [outcome, setOutcome] = useState(null);  // { created, duplicates, errors, total_rows, dry_run }

  const pick = (e) => {
    setFile(e.target.files?.[0] || null);
    setOutcome(null);
  };

  const run = async (dryRun) => {
    if (!file) {
      toast.error('Pick a file first');
      return;
    }
    setRunning(true);
    setOutcome(null);
    try {
      const result = await bulkImportBrokerages(token, file, { dryRun });
      // Dry-run shows the outcome panel and stays open — the whole
      // point of a dry-run is to review the result before deciding.
      // Real Import surfaces the breakdown in a richer toast and
      // closes the modal so the user is dropped back on the live
      // brokerage list right away.
      if (dryRun) {
        setOutcome(result);
        toast.info(
          `Dry-run: ${result.created} would import, ` +
          `${result.duplicates?.length || 0} duplicates, ` +
          `${result.errors?.length || 0} errors.`,
        );
      } else {
        // Build a multi-line toast description so the user sees the
        // full breakdown without re-opening the modal.
        const parts = [];
        if (result.duplicates?.length) {
          parts.push(`${result.duplicates.length} duplicate${result.duplicates.length === 1 ? '' : 's'} skipped`);
        }
        if (result.errors?.length) {
          parts.push(`${result.errors.length} error${result.errors.length === 1 ? '' : 's'}`);
        }
        const desc = parts.length ? parts.join(' · ') : undefined;
        toast.success(
          `Imported ${result.created} brokerage${result.created === 1 ? '' : 's'}.`,
          desc ? { description: desc } : undefined,
        );
        onImported?.();
        // Defer the close one tick so the toast registers first; if
        // we closed and unmounted synchronously the toast call could
        // race with the parent re-render.
        setTimeout(() => onOpenChange(false), 0);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Import failed');
    } finally {
      setRunning(false);
    }
  };

  const reset = () => {
    setFile(null);
    setOutcome(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload size={18} className="text-violet-500" />
            Bulk import brokerages
          </DialogTitle>
          <DialogDescription>
            Upload a CSV or Excel file. Every row is checked against the existing
            brokerages — duplicates (by name, RERA, trade licence, email or phone) are
            reported, not imported.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-4">
          {/* Template download — first thing the user sees so they
              know what shape we expect. */}
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold text-slate-700">
                  Need the right column layout?
                </div>
                <div className="text-xs text-slate-500">
                  Download a pre-formatted template — header row + one example row.
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open(importTemplateXlsxUrl(token), '_blank')}
              >
                <Download size={13} className="mr-1.5" /> Template
              </Button>
            </div>
          </div>

          {/* File picker */}
          <div className="rounded-lg border-2 border-dashed border-slate-300 bg-white p-4">
            <div className="flex items-center justify-center gap-3">
              <FileSpreadsheet size={20} className="text-slate-400" />
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xlsm,.tsv"
                onChange={pick}
                className="text-sm text-slate-600 file:mr-3 file:rounded-md file:border file:border-slate-200 file:bg-slate-100 file:px-3 file:py-1.5 file:text-xs file:font-medium file:text-slate-700 hover:file:bg-slate-200"
              />
            </div>
            {file && (
              <div className="mt-2 text-center text-xs text-slate-500">
                Selected: <strong className="text-slate-700">{file.name}</strong>
                {' '}({(file.size / 1024).toFixed(1)} KB)
              </div>
            )}
          </div>

          {/* Outcome panel */}
          {outcome && (
            <div className="space-y-2">
              <div className="grid grid-cols-3 gap-2">
                <OutcomeTile
                  icon={CheckCircle2}
                  tone="emerald"
                  label={outcome.dry_run ? 'Would import' : 'Imported'}
                  value={outcome.created}
                  info={outcome.dry_run
                    ? 'Rows that would be successfully inserted on a real Import — they passed every dedup check and had all required fields.'
                    : 'Rows successfully inserted into the brokers collection. Each new firm is also stamped with normalized identifier fields used by future dedup checks.'}
                />
                <OutcomeTile
                  icon={AlertTriangle}
                  tone="amber"
                  label="Duplicates"
                  value={outcome.duplicates?.length || 0}
                  info="Rows skipped because the name, RERA, trade licence, email, or phone matched an existing brokerage. The chip on each duplicate row shows which field collided. Action: rename / clean the source row, or accept the existing record."
                />
                <OutcomeTile
                  icon={XCircle}
                  tone="rose"
                  label="Errors"
                  value={outcome.errors?.length || 0}
                  info="Rows the importer couldn't process at all — usually a missing brokerage name, malformed field, or unexpected exception. Each error row shows the reason in red."
                />
              </div>

              {outcome.duplicates?.length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <div className="mb-1.5 flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-amber-700">
                    Duplicates skipped ({outcome.duplicates.length})
                    <InfoBadge text="Each row lists the file row number, the brokerage name from your file, the existing firm it collides with, and (in the chip on the right) WHICH identifier matched. Hover the chip for what that field check actually does." />
                  </div>
                  <ul className="max-h-44 space-y-1 overflow-y-auto text-xs">
                    {outcome.duplicates.slice(0, 50).map((d, i) => (
                      <li key={i} className="flex items-center gap-2 rounded bg-white/70 px-2 py-1">
                        <Badge variant="outline" className="text-[10px]">Row {d.row_index}</Badge>
                        <span className="font-medium text-slate-800">{d.name}</span>
                        <span className="text-slate-500">
                          → conflicts with <strong>{d.existing_name}</strong>
                        </span>
                        {/* Hover the chip to see the explanation of
                            which field check caught this row. The
                            ``title=`` attribute on the Badge gives a
                            native browser tooltip without bringing in
                            a popover library. */}
                        <Badge
                          variant="outline"
                          title={CONFLICT_FIELD_INFO[d.conflict_field] || `Matched on ${d.conflict_field}`}
                          className="ml-auto text-[10px] bg-amber-100 border-amber-200 cursor-help"
                        >
                          {d.conflict_field}
                        </Badge>
                      </li>
                    ))}
                    {outcome.duplicates.length > 50 && (
                      <li className="text-[11px] text-slate-500">
                        … and {outcome.duplicates.length - 50} more
                      </li>
                    )}
                  </ul>
                </div>
              )}

              {outcome.errors?.length > 0 && (
                <div className="rounded-lg border border-rose-200 bg-rose-50 p-3">
                  <div className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-rose-700">
                    Errors ({outcome.errors.length})
                  </div>
                  <ul className="max-h-32 space-y-1 overflow-y-auto text-xs">
                    {outcome.errors.slice(0, 20).map((er, i) => (
                      <li key={i} className="rounded bg-white/70 px-2 py-1">
                        <Badge variant="outline" className="mr-2 text-[10px]">Row {er.row_index}</Badge>
                        <span className="text-rose-700">{er.error}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={running}>
            {outcome ? 'Close' : 'Cancel'}
          </Button>
          <Button
            variant="outline"
            onClick={() => run(true)}
            disabled={!file || running}
            title="Validate the file without inserting anything"
          >
            {running ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : null}
            Dry run
          </Button>
          <Button
            onClick={() => run(false)}
            disabled={!file || running}
            className="bg-violet-600 hover:bg-violet-700"
          >
            {running ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Upload size={14} className="mr-1.5" />}
            Import
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const TILE_TONES = {
  emerald: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
  amber:   { bg: 'bg-amber-50',   text: 'text-amber-700',   border: 'border-amber-200' },
  rose:    { bg: 'bg-rose-50',    text: 'text-rose-700',    border: 'border-rose-200' },
};

const OutcomeTile = ({ icon: Icon, tone, label, value, info }) => {
  const t = TILE_TONES[tone] || TILE_TONES.emerald;
  return (
    <div className={`rounded-lg border ${t.border} ${t.bg} p-3`}>
      <div className={`flex items-center gap-1.5 text-[11px] font-medium ${t.text}`}>
        <Icon size={12} /> <span className="flex-1">{label}</span>
        <InfoBadge text={info} />
      </div>
      <div className="mt-0.5 text-2xl font-bold text-slate-800">{value}</div>
    </div>
  );
};
